'use client';

import { useMedia } from '@/context/MediaContext';
import { motion } from 'motion/react';
import { useCallback, useMemo } from 'react';

const buttonVariants = {
  initial: {
    backgroundColor: 'transparent',
  },
  hover: {
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    transition: { duration: 0.2 }
  },
  tap: {
    scale: 0.95,
    transition: { duration: 0.1 }
  }
};

const MediaPlayerButton = ({
  style = 'stream',
  type,
  media,
  metadata,
}: {
  style?: 'podcast' | 'stream';
  type: 'audio' | 'video' | 'stream';
  media: string;
  metadata: {
    title: string;
    poster: string;
  }
}) => {

  const { actions, state } = useMedia();
  const { isPlaying, currentMedia, currentTime, duration } = state;
  const { showPlayer, hidePlayer, setPlayingState } = actions;

  const isCurrentMediaPlaying = useMemo(
    () => currentMedia === media && isPlaying,
    [currentMedia, media, isPlaying]
  );

  const handlePlayPause = useCallback(() => {
    if (currentMedia === media) {
      setPlayingState(!isPlaying);
      return;
    }

    if (isCurrentMediaPlaying) {
      setPlayingState(false);
    }

    hidePlayer();

    setTimeout(() => {
      showPlayer(media, {
        title: metadata.title,
        cover: metadata.poster,
        type: type
      });
      setPlayingState(true);
    }, 100);
  }, [currentMedia, media, metadata, isPlaying, showPlayer, setPlayingState]);

  if (style === 'podcast') {
    return (
      <div className="flex flex-col items-center">
        <motion.button
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '24px',
            height: '24px',
            backgroundColor: isPlaying ? '#eef4ff' : '#f3f4f6',
            color: isPlaying ? '#2563eb' : '#525252'
          }}
          whileHover={{
            backgroundColor: isPlaying ? '#dbeafe' : '#e5e7eb',
            scale: 1.05
          }}
          whileTap={{ scale: 0.95 }}
          onClick={() => handlePlayPause()}
          aria-label={isPlaying ? "Pause audio" : "Play audio"}
        >
          {isPlaying && currentMedia === media ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="4" width="4" height="16" rx="1" />
              <rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (

            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </motion.button>
      </div>
    );
  }


  return (
    <motion.button
      className="relative"
      variants={buttonVariants}
      initial="initial"
      whileHover="hover"
      whileTap="tap"
      onClick={() => handlePlayPause()}
    >
      <svg height="24" width="24" viewBox="0 0 16 16" style={{ color: 'var(--color-icon-inverse)' }}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M1.5 2.5H14.5V12.5C14.5 13.0523 14.0523 13.5 13.5 13.5H2.5C1.94772 13.5 1.5 13.0523 1.5 12.5V2.5ZM0 1H1.5H14.5H16V2.5V12.5C16 13.8807 14.8807 15 13.5 15H2.5C1.11929 15 0 13.8807 0 12.5V2.5V1Z"
          fill="currentColor"
        />
        {isPlaying && currentMedia == media ? (
          <g>
            <rect x="5" y="5" width="2" height="6" fill="currentColor" />
            <rect x="9" y="5" width="2" height="6" fill="currentColor" />
          </g>
        ) : (
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.745 6.76445L9.2161 8L6.745 9.23555V6.76445ZM11.1056 7.55279C11.4741 7.73705 11.4741 8.26295 11.1056 8.44721L6.22361 10.8882C5.89116 11.0544 5.5 10.8127 5.5 10.441L5.5 5.55902C5.5 5.18733 5.89116 4.94558 6.22361 5.1118L11.1056 7.55279Z"
            fill="currentColor"
          />
        )}
      </svg>
    </motion.button>
  );
};

export default MediaPlayerButton;